import { maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { Account } from '@reliance/types/models/Account';
import { Facility } from '@reliance/types/models/Facility';
import { SpecialCondition } from '@reliance/types/models/SpecialCondition';
import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Field, Form } from 'react-final-form';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';
import { IStatusResponse } from '../common/reliance-api-service';
import { Select } from '../form/Select';
import { TextInput } from '../form/TextInput';

type AddEditProps = {
  account?: Account;
  facility?: Facility;
  condition?: SpecialCondition;
  facilities: Facility[];
  onClose(success?: boolean): void;
};

type AddEditConditionData = {
  id: number | undefined;
  ani: string;
  condition: string;
  facilityId: number;
  description: string;
};

const mapCreateError = (err: AxiosError<{ error?: { error?: string } }>): string | null => {
  const code = err.response?.data?.error?.error;
  if (code === 'condition_invalid') {
    return 'The condition selected is invalid';
  }
  if (code === 'facility_not_found') {
    return 'The facility selected is invalid';
  }
  if (code === 'account_not_found') {
    return 'The phone number supplied is invalid';
  }
  return null;
};

const save = (data: AddEditConditionData, props: AddEditProps) => {
  if (data.id) {
    return api.put(`/condition/${data.id}`, data).catch((err) => {
      return { [FORM_ERROR]: err };
    });
  }
  const aniSource = props.account?.phoneNumber ?? data.ani;
  const ani = aniSource.replace(/[^0-9]/g, '');
  const params: Record<string, string | number> = {
    condition: data.condition,
    description: data.description ?? '',
  };
  if (data.facilityId != null) {
    params.facilityId = data.facilityId;
  }
  return api
    .post<IStatusResponse & { duplicate?: boolean; id?: number }>(
      `/condition/${encodeURIComponent(ani)}`,
      null,
      { params }
    )
    .catch((err: AxiosError<{ error?: { error?: string } }>) => {
      if (err.response?.status === 400) {
        const msg = mapCreateError(err);
        if (msg) {
          return { [FORM_ERROR]: msg };
        }
      }
      return { [FORM_ERROR]: err };
    });
};

const AddEdit: React.FC<AddEditProps> = (props) => {
  const initialValues: AddEditConditionData = {
    id: props.condition?.id,
    ani: props.account?.phoneNumber ?? '',
    condition: null,
    facilityId: props.facility?.id ?? null,
    description: '',
  };

  const aniRules = props.facility ? [required] : [];

  if (props.account == null && props.facility == null) {
    return null;
  }

  return (
    <Modal show={true} onHide={props.onClose}>
      <Form
        initialValues={initialValues}
        keepDirtyOnReinitialize={true}
        onSubmit={(data: AddEditConditionData) =>
          save(data, props).then((response) => {
            if (response != null && typeof response === 'object' && FORM_ERROR in response) {
              return;
            }
            if (response && 'data' in response && response.data) {
              const body = response.data as IStatusResponse & { duplicate?: boolean };
              if (body.duplicate) {
                alertStore.warning('This condition was created previously', true);
              }
              props.onClose(true);
            }
          })
        }
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) =>
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              {submitError &&
                <Alert variant="danger">{String(submitError)}</Alert>
              }

              {props.facility &&
                <Row>
                  <Col>
                    <TextInput
                      label="Phone number"
                      name="ani"
                      validationRules={aniRules}
                    />
                  </Col>
                </Row>
              }

              <Row className="mb-2">
                <Col>
                  <Select
                    validationRules={[required]}
                    name="condition"
                    label="Condition">
                    <option></option>
                    <option value="free">Free Number</option>
                    <option value="excluded">Excluded Number</option>
                    <option value="allow_dtmf">Allow DTMF</option>
                    <option value="allow_multiple_calls">Allow Multiple Calls</option>
                    <option value="announcement_skip">Call Announcement - Skip</option>
                    <option value="announcement_delay_10">Call Announcement - Delay 10</option>
                  </Select>
                </Col>

                <Col>
                  {props.facility ? (
                    <>
                      <Field name="facilityId" render={({ input }) => <input {...input} type="hidden" />} />
                      <div className="mb-2">
                        <span className="form-label">Facility</span>
                        <div>{props.facility.name} ({props.facility.city}, {props.facility.state})</div>
                      </div>
                    </>
                  ) : (
                    <Select
                      name="facilityId"
                      label="Facility">
                      <option></option>
                      {(props.facilities || []).map((facility) =>
                        <option
                          value={facility.id}
                          key={`special-condition-facility-${facility.id}`}>
                          {facility.name} ({facility.city}, {facility.state})
                        </option>
                      )}
                    </Select>
                  )}
                </Col>
              </Row>

              <Row>
                <Col>
                  <TextInput
                    label="Description"
                    validationRules={[required, minLength(1), maxLength(250)]}
                    name="description" />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="default"
                type="button"
                disabled={submitting}
                onClick={() => props.onClose()}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={submitting || hasValidationErrors}
                type="submit">
                Save
              </Button>
            </Modal.Footer>
          </form>
        } />
    </Modal>
  );
};

export default AddEdit;
